import React from "react";
import { formatInTimeZone } from "date-fns-tz";
import { HoverTooltip } from "@app/dashboard/src/components/shared/HoverTooltip";
import { ClientEndpointHealthWindow, HealthBucket, LatencyBand } from "@app/shared";
import { formatRate, formatWindowSpan } from "./endpointHealth";

/**
 * The delivery-health strip and window rate for one client endpoint: what the status dot beside it cannot
 * say, which is whether an endpoint that is working right now has been working all along.
 *
 * The dot answers "is it delivering now" from the last few sends. This answers "how often does it fail",
 * which is the question behind "it mostly works but drops one now and then" — the case where a green dot
 * and a red dot are equally misleading.
 *
 * Rendering rules, all of which exist because the obvious version of each is wrong:
 *  - a rate is never shown without its denominator and span, because endpoint cadences differ by three
 *    orders of magnitude and "99%" of 7 sends is not comparable to "99%" of 40,000
 *  - below a handful of sends the fraction is shown instead of a percentage, rather than claiming 100%
 *    off three data points
 *  - an hour with no sends is grey, never green: nothing was attempted, so nothing succeeded
 *  - latency is a band, because that is the resolution the stored histogram actually has
 */

// The --*-icon toast colors are tuned for large icons and fail WCAG at this size (orange measures
// 1.97:1 on white, red 4.0:1). Everything here is either 12px text or a 4px-wide bar that carries the
// meaning of the row, so both the 4.5:1 text threshold and the 3:1 graphics threshold apply. These
// tokens are the accessible variants; see variables.css for the measurements.
const SUCCESS_COLOR = "var(--success-text)";
const ERROR_COLOR = "var(--error-text)";
const WARNING_COLOR = "var(--warning-text)";
const MUTED_COLOR = "var(--text-body-muted)";
// A bar for an hour with no sends still carries meaning ("nothing was attempted"), so it is a graphic
// under WCAG 1.4.11 and needs 3:1. --text-body-muted is 2.32:1 on white and was additionally being faded
// to 35% opacity, which put it far below anything visible. This is 3.36:1 and still reads as "inactive"
// beside the saturated bars.
const EMPTY_BAR_COLOR = "#8c8c8c";

/** "1.2s", "850ms", "30s+" — a bare millisecond count is unreadable across four orders of magnitude. */
const humanizeMs = (ms: number): string => (ms >= 1000 ? `${+(ms / 1000).toFixed(ms >= 10000 ? 0 : 1)}s` : `${Math.round(ms)}ms`);

/** A latency band as text: "2–5s", or "over 30s" for the open-ended top bucket. */
export const formatBand = (band: LatencyBand | undefined | null): string | undefined => {
    if (!band) return undefined;
    if (band.highMs === undefined || band.highMs === null) return `over ${humanizeMs(band.lowMs)}`;
    if (band.lowMs === 0) return `under ${humanizeMs(band.highMs)}`;
    return `${humanizeMs(band.lowMs)}–${humanizeMs(band.highMs)}`;
};

const bucketColor = (b: HealthBucket): string => {
    if (b.sends === 0) return EMPTY_BAR_COLOR;  // nothing attempted — not a success
    if (b.failures === 0) return b.slowSends > 0 ? WARNING_COLOR : SUCCESS_COLOR;
    if (b.failures === b.sends) return ERROR_COLOR;
    return WARNING_COLOR;                        // partial failure: the case this whole strip exists for
};

const zonedHour = (value: Date | string | undefined, zone: string): string => {
    if (!value) return "unknown time";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return "unknown time";
    try {
        // Zone abbreviation always spelled out — a bare clock time is ambiguous to anyone not sitting in
        // the facility's timezone, which is most people reading this.
        return formatInTimeZone(d, zone, "MMM d, HH:mm zzz");
    } catch {
        return d.toISOString();
    }
};

const BucketTooltip: React.FC<{ bucket: HealthBucket; zone: string }> = ({ bucket, zone }) => {
    const p95 = formatBand(bucket.p95Ms);
    return (
        <div className="text-xs whitespace-nowrap">
            <div className="font-semibold">{zonedHour(bucket.hourUtc, zone)}</div>
            {bucket.sends === 0 ? (
                <div className="text-muted-body-text">no sends</div>
            ) : (
                <>
                    <div>
                        {bucket.sends} send{bucket.sends === 1 ? "" : "s"}
                        {bucket.failures > 0 && <>, {bucket.failures} failed</>}
                        {bucket.slowSends > 0 && <>, {bucket.slowSends} slow</>}
                    </div>
                    {p95 && <div className="text-muted-body-text">p95 {p95}</div>}
                    {/* Says so rather than silently offering nothing, so an empty history dialog is never a surprise. */}
                    {!bucket.hasRawDetail && <div className="text-muted-body-text">detail expired</div>}
                </>
            )}
        </div>
    );
};

/**
 * Wraps the status line so hovering it reveals the most recent failure.
 *
 * It lives on the status line rather than on a line of its own because the two say complementary things
 * about the same thing: the line states what delivery is doing NOW, and the most recent failure is the
 * obvious follow-up question — but only sometimes, and a permanent line for a sometimes-question is how
 * a row grows to six lines. Note this is shown whether or not the endpoint is currently failing: an
 * endpoint that dropped one delivery two hours ago and recovered otherwise looks identical to one that
 * has never failed at all, and the status line itself only speaks while a failure streak is open.
 *
 * Renders children untouched when there is no failure to report, so it never adds a dead hover target.
 */
export const LastFailureHover: React.FC<React.PropsWithChildren<{ health?: ClientEndpointHealthWindow; zone: string }>> = ({ health, zone, children }) => {
    if (!health?.lastFailureUtc) return <>{children}</>;
    return (
        <HoverTooltip placement="top-start" hoverable={<div className="cursor-help">{children}</div>}>
            <div className="text-xs max-w-md">
                <div className="font-semibold">last failure {zonedHour(health.lastFailureUtc, zone)}</div>
                {health.lastFailureMessage && <div className="break-words mt-0.5">{health.lastFailureMessage}</div>}
            </div>
        </HoverTooltip>
    );
};

export const EndpointHealthStrip: React.FC<{ health?: ClientEndpointHealthWindow; zone: string }> = ({ health, zone }) => {
    // Absent is "no information", never "fine" — the same contract deliveryHealth() keeps. The webconfig
    // has no rollup to send, so it lands here and renders nothing at all.
    if (!health) return null;

    const rate = formatRate(health);
    const span = formatWindowSpan(health);
    const p95 = formatBand(health.p95Ms);
    const buckets = health.buckets ?? [];
    const outcomes = health.topFailureOutcomes ?? [];

    // An endpoint with no sends anywhere in the window gets a line saying so, not silence. Rendering
    // nothing makes "no deliveries in 30 days" and "this feature isn't working" look identical, which
    // costs far more than the line does — an endpoint quietly sending nothing is itself worth seeing.
    if (health.sends === 0 && buckets.every((b) => b.sends === 0)) {
        return (
            <div className="text-xs mt-1 text-muted-body-text">
                no deliveries{span ? ` in the last ${span}` : ""}
            </div>
        );
    }

    // The breakdown is detail, not headline: it only matters once someone has decided the rate is worth
    // looking into, and on its own line it cost a row of vertical space in every endpoint.
    const rateText = rate && (
        <span style={{ color: health.failures > 0 ? WARNING_COLOR : MUTED_COLOR }}>
            {rate}
            {span && <span className="text-muted-body-text"> · {span}</span>}
        </span>
    );

    return (
        <div className="text-xs mt-1 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
                {rate && (outcomes.length > 0 ? (
                    <HoverTooltip
                        placement="top-start"
                        // Dotted underline so there is something to tell the reader a hover exists; an
                        // unmarked hover target is a hidden feature.
                        hoverable={<span className="underline decoration-dotted underline-offset-2 cursor-help">{rateText}</span>}
                    >
                        <div className="text-xs whitespace-nowrap">
                            <div className="font-semibold">
                                {health.failures.toLocaleString()} failure{health.failures === 1 ? "" : "s"}
                            </div>
                            {outcomes.map((o) => (
                                <div key={o.label}>{o.label} × {o.count.toLocaleString()}</div>
                            ))}
                        </div>
                    </HoverTooltip>
                ) : rateText)}
                {p95 && <span className="text-muted-body-text">p95 {p95}</span>}
            </div>

            {buckets.length > 0 && (
                <div className="flex items-end gap-px" role="img" aria-label={`Delivery health per hour: ${rate ?? "no sends"}`}>
                    {buckets.map((b) => (
                        <HoverTooltip
                            key={typeof b.hourUtc === "string" ? b.hourUtc : b.hourUtc?.toISOString()}
                            placement="top"
                            hoverable={
                                <span
                                    className="inline-block w-1 h-4 rounded-sm"
                                    style={{ backgroundColor: bucketColor(b) }}
                                />
                            }
                        >
                            <BucketTooltip bucket={b} zone={zone} />
                        </HoverTooltip>
                    ))}
                </div>
            )}
        </div>
    );
};
